'use client';

import type { Swiper as SwiperCore } from 'swiper';
import type { HomeWidgetConverter } from '@/app/_shared/converter/home-widget-converter';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useRef, useState, type FC } from 'react';
import { SubHeader } from '@yanolja-rnd/nol-design-system';
import { useAtomValue } from 'jotai';
import { clientLogStandardized } from '@weberus/client-tracker';
import { ShowAllButton } from 'app/_components/common/show-all-button';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { TabArea } from './tab-area';
import { ProductArea } from './product-area';

type ConvertedTopRatedProductRankingData = ReturnType<
  typeof HomeWidgetConverter.YDM__NOL_TOP_RATED_PRODUCT_RANKING__V1
>;

/*
 * @see {@link https://yanolja-dev-cx-api-bucket.s3.ap-northeast-2.amazonaws.com/api-document/cx-display-api/classes/controller_widget_top_rated_product_ranking_dto_widget_top_rated_product_ranking_response_dto.WidgetTopRatedProductRankingResponseDto.html API 문서}
 */
export interface TopRatedProductRankingProps {
  data: ConvertedTopRatedProductRankingData;
  pageNameLog: HomeLogPageNameValue;
}

/**
 *  상품형 탭 | 랭킹
 *
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=3235-21410&m=dev}
 */
export const TopRatedProductRanking: FC<TopRatedProductRankingProps> = ({ pageNameLog, data }) => {
  const { header, items, logMeta: widgetLogMeta } = data;
  const { isPcWidth } = useAtomValue(pcWidthAtom);

  const swiperRef = useRef<SwiperCore | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onChangeTab = (index: number) => {
    setSelectedIndex(index);
    isPcWidth ? swiperRef.current?.slideToLoop(index) : swiperRef.current?.slideTo(index);
    clientLogStandardized('click', pageNameLog, { ...data.logMeta, ...data.items[index].logMeta, position: index });
  };

  const tabLabels = items.filter((i) => !!i.header?.title).map((i) => i.header?.title) as string[];
  const hasTab = tabLabels.length > 0;

  const moreObj = hasTab ? items[selectedIndex]?.header?.more : header?.more;
  const moreTitle = moreObj?.title;
  const moreHref = moreObj?.action?.web;

  const isRankItem = !!items?.[0].items?.[0].rank;

  if (!isRankItem) {
    return null;
  }

  return (
    <div className="my-16 w-full pc:my-40 pc:w-auto pc:max-w-pc-size">
      {!!header?.title && <SubHeader className="px-16" title={header.title} subTitle={header.subTitle} />}
      {!!hasTab && <TabArea tabLabels={tabLabels} selectedIndex={selectedIndex} onChangeTab={onChangeTab} />}
      <ProductArea
        pageNameLog={pageNameLog}
        widgetLogMeta={widgetLogMeta}
        tabItems={items.map((item) => item.items)}
        tabLogMeta={items[selectedIndex].logMeta}
        setSelectedIndex={setSelectedIndex}
        swiperRef={swiperRef}
      />
      {!!moreTitle && !!moreHref && (
        <ShowAllButton
          boldTitle={hasTab ? items[selectedIndex].header?.title : undefined}
          title={moreTitle}
          href={moreHref}
          onClick={() =>
            clientLogStandardized('click', pageNameLog, {
              ...items[selectedIndex].logMeta,
              ...items[selectedIndex].header?.more?.logMeta,
              objectContainer: widgetLogMeta.objectContainer,
              object: 'seeAllBtn',
              objectType: 'seeAllBtn',
            })
          }
        />
      )}
    </div>
  );
};
